<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div v-if="showModal" class="modal-overlay" v-on:click.self="close">
        <transition name="slide-in">
          <div v-if="showModal" class="modal-panel">
            <header class="modal-head">
              <div>
                <div class="modal-eyebrow">
                  <span class="status-dot"></span>
                  {{ isEdit ? 'Edit plan' : 'New plan' }}
                </div>
                <h2>{{ isEdit ? form.name || 'Edit plan' : 'Create plan' }}</h2>
              </div>
              <button type="button" class="icon-button" v-on:click="close" aria-label="Close">
                <font-awesome-icon icon="fa-solid fa-xmark" />
              </button>
            </header>

            <form class="modal-body" v-on:submit.prevent="save">
              <section class="form-section">
                <h3>Details</h3>
                <div class="field-grid">
                  <div class="field field--full">
                    <label>Name <span class="req">*</span></label>
                    <input v-model.trim="form.name" type="text" class="form-control shadow-none" placeholder="e.g. Pro" />
                  </div>
                  <div class="field">
                    <label>Total price (ETB) <span class="req">*</span></label>
                    <input v-model="form.totalPrice" type="number" min="0" step="0.01" class="form-control shadow-none" placeholder="0.00" />
                  </div>
                  <div class="field">
                    <label>Duration (days) <span class="req">*</span></label>
                    <input v-model="form.duration" type="number" min="1" step="1" class="form-control shadow-none" placeholder="30" />
                  </div>
                </div>
              </section>

              <section class="form-section">
                <h3>Usage limits</h3>
                <div class="field-grid">
                  <div class="field">
                    <label>Verification limit</label>
                    <input v-model="form.verificationLimit" type="number" min="1" step="1" class="form-control shadow-none" placeholder="Unlimited" />
                    <small class="field-hint">Leave blank for unlimited.</small>
                  </div>
                  <div class="field">
                    <label>Limit period</label>
                    <select v-model="form.limitPeriod" class="form-control shadow-none">
                      <option value="daily">Daily</option>
                      <option value="total">Total (lifetime)</option>
                    </select>
                  </div>
                  <div class="field">
                    <label>Team seats</label>
                    <input v-model="form.maxUsers" type="number" min="1" step="1" class="form-control shadow-none" placeholder="Unlimited" />
                  </div>
                  <div class="field">
                    <label>Max devices</label>
                    <input v-model="form.maxDevices" type="number" min="1" step="1" class="form-control shadow-none" placeholder="Unlimited" />
                  </div>
                </div>
              </section>

              <section class="form-section">
                <h3>Catalogue</h3>
                <div class="field field--full">
                  <label>Features</label>
                  <textarea v-model="form.featuresText" rows="4" class="form-control shadow-none" placeholder="One feature per line"></textarea>
                  <small class="field-hint">One feature per line — shown on the subscription wall.</small>
                </div>
                <div class="toggles">
                  <label class="toggle">
                    <input type="checkbox" v-model="form.isUserSelectable" />
                    <span>User selectable</span>
                  </label>
                  <label class="toggle">
                    <input type="checkbox" v-model="form.isFree" />
                    <span>Free / trial plan</span>
                  </label>
                </div>
              </section>
            </form>

            <footer class="modal-foot">
              <button type="button" class="ghost-button" :disabled="saving" v-on:click="close">Cancel</button>
              <button type="button" class="primary-button" :disabled="saving" v-on:click="save">
                <span v-if="saving" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span v-else>{{ isEdit ? 'Save changes' : 'Create plan' }}</span>
              </button>
            </footer>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script>
import Subscription from "@/services/subscription";
import { TOAST_GLOBAL_CONFIG } from "@/utils/const";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

library.add(faXmark);

export default {
  name: "SubscriptionPlanModal",
  props: {
    // null/undefined → create mode; an object → edit that plan.
    plan: { type: Object, default: null },
  },
  emits: ["close", "saved"],
  data() {
    return {
      showModal: true,
      saving: false,
      form: {
        name: "",
        totalPrice: "",
        duration: "",
        verificationLimit: "",
        limitPeriod: "daily",
        maxUsers: "",
        maxDevices: "",
        isFree: false,
        isUserSelectable: true,
        featuresText: "",
      },
    };
  },
  computed: {
    isEdit() {
      return !!(this.plan && this.plan.id);
    },
  },
  methods: {
    numOrNull(v) {
      return v === "" || v === null || v === undefined ? null : Number(v);
    },
    parsedFeatures() {
      return this.form.featuresText
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
    },
    populate() {
      if (!this.isEdit) return;
      const p = this.plan;
      this.form = {
        name: p.name || "",
        totalPrice: p.totalPrice ?? "",
        duration: p.duration ?? "",
        verificationLimit: p.verificationLimit ?? "",
        limitPeriod: p.limitPeriod || "daily",
        maxUsers: p.maxUsers ?? "",
        maxDevices: p.maxDevices ?? "",
        isFree: !!p.isFree,
        isUserSelectable: p.isUserSelectable !== false,
        featuresText: Array.isArray(p.features) ? p.features.join("\n") : "",
      };
    },
    validate() {
      if (!this.form.name.trim()) {
        this.$toast.error("A plan name is required.", TOAST_GLOBAL_CONFIG);
        return false;
      }
      if (!(Number(this.form.totalPrice) > 0)) {
        this.$toast.error("Total price must be greater than zero.", TOAST_GLOBAL_CONFIG);
        return false;
      }
      if (!(Number(this.form.duration) > 0)) {
        this.$toast.error("Duration must be a positive number of days.", TOAST_GLOBAL_CONFIG);
        return false;
      }
      return true;
    },
    async save() {
      if (this.saving) return;
      if (!this.validate()) return;

      const payload = {
        name: this.form.name.trim(),
        totalPrice: Number(this.form.totalPrice),
        duration: Number(this.form.duration),
        verificationLimit: this.numOrNull(this.form.verificationLimit),
        limitPeriod: this.form.limitPeriod,
        maxUsers: this.numOrNull(this.form.maxUsers),
        maxDevices: this.numOrNull(this.form.maxDevices),
        isFree: this.form.isFree,
        isUserSelectable: this.form.isUserSelectable,
        features: this.parsedFeatures(),
      };

      this.saving = true;
      try {
        const res = this.isEdit
          ? await Subscription.editSubscriptionPlan({ id: this.plan.id, ...payload })
          : await Subscription.addSubscriptionPlan(payload);

        if (res?.data?.status) {
          this.$toast.success(this.isEdit ? "Plan updated." : "Plan created.", TOAST_GLOBAL_CONFIG);
          this.$emit("saved");
          this.close();
        }
      } finally {
        this.saving = false;
      }
    },
    close() {
      this.$emit("close");
    },
    onKeydown(event) {
      if (event.key === "Escape") this.close();
    },
  },
  mounted() {
    this.populate();
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeUnmount() {
    document.body.style.overflow = this.previousBodyOverflow || "";
    window.removeEventListener("keydown", this.onKeydown);
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}

.modal-panel {
  width: 540px;
  max-width: 95vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-left: 1px solid var(--ehs-color-border);
  box-shadow: -24px 0 60px rgba(15, 23, 42, 0.18);
  color: var(--ehs-color-ink);
}

.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid var(--ehs-color-border);
}

.modal-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 5px 11px;
  margin-bottom: 8px;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-pill);
  background: var(--ehs-color-primary-soft);
  color: var(--ehs-color-primary-active);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ehs-color-primary);
}

.modal-head h2 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.025em;
  text-transform: capitalize;
}

.icon-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--ehs-color-border);
  background: #ffffff;
  border-radius: 50%;
  color: var(--ehs-color-ink);
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.icon-button:hover {
  background: var(--ehs-color-primary-soft);
  color: var(--ehs-color-primary-hover);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 22px 24px 32px;
  background: var(--ehs-color-canvas);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-section {
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-lg);
}

.form-section h3 {
  margin: 0 0 14px;
  font-family: var(--ehs-font-display);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--ehs-color-muted);
}

.field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.field--full {
  grid-column: 1 / -1;
}

.field label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--ehs-color-text);
}

.field .req {
  color: var(--ehs-color-danger);
}

.field-hint {
  font-size: 0.7rem;
  color: var(--ehs-color-soft);
}

textarea.form-control {
  resize: vertical;
  min-height: 84px;
}

.toggles {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 14px;
}

.toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--ehs-color-ink);
  cursor: pointer;
  margin: 0;
}

.toggle input {
  width: 16px;
  height: 16px;
  accent-color: var(--ehs-color-primary);
  cursor: pointer;
}

.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--ehs-color-border);
  background: #ffffff;
}

.ghost-button,
.primary-button {
  border-radius: var(--ehs-radius-pill);
  border: 1px solid transparent;
  padding: 10px 20px;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.ghost-button {
  background: #ffffff;
  color: var(--ehs-color-ink);
  border-color: var(--ehs-color-border);
}

.ghost-button:hover:not(:disabled) {
  background: var(--ehs-color-canvas);
  border-color: var(--ehs-color-border-strong);
}

.primary-button {
  background: var(--ehs-color-primary);
  color: #ffffff;
  border-color: var(--ehs-color-primary);
}

.primary-button:hover:not(:disabled) {
  background: var(--ehs-color-primary-hover);
  border-color: var(--ehs-color-primary-hover);
}

.ghost-button:disabled,
.primary-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateX(100%);
}

@media (max-width: 991.98px) {
  .modal-panel {
    width: 100%;
    max-width: 100%;
  }

  .field-grid {
    grid-template-columns: 1fr;
  }

  .modal-foot {
    flex-wrap: wrap;
  }
}
</style>
